import dayjs from 'dayjs';
import {
  OCRMetrics,
  OCRMetricsDataPoint,
  OCRMetricsResponse,
  OCRTimeRange,
} from '@/service/gpu-instance/ocr-type';

// Helper function to generate random number within range
const randomInRange = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

// Generate mock metrics data points based on time range
export const generateMockMetricsDataPoints = (
  timeRange: OCRTimeRange,
  customMonth?: string
): OCRMetricsDataPoint[] => {
  const dataPoints: OCRMetricsDataPoint[] = [];
  let startTime: dayjs.Dayjs;
  let endTime: dayjs.Dayjs;
  let intervalMinutes: number;

  switch (timeRange) {
    case '24h':
      endTime = dayjs();
      startTime = endTime.subtract(24, 'hour');
      intervalMinutes = 60; // 1 data point per hour
      break;
    case '7d':
      endTime = dayjs();
      startTime = endTime.subtract(7, 'day');
      intervalMinutes = 360; // 1 data point per 6 hours
      break;
    case '30d':
      endTime = dayjs();
      startTime = endTime.subtract(30, 'day');
      intervalMinutes = 1440; // 1 data point per day
      break;
    case 'month':
      if (customMonth) {
        startTime = dayjs(customMonth).startOf('month');
        endTime = dayjs(customMonth).endOf('month');
      } else {
        startTime = dayjs().startOf('month');
        endTime = dayjs().endOf('month');
      }
      intervalMinutes = 1440; // 1 data point per day
      break;
    default:
      endTime = dayjs();
      startTime = endTime.subtract(24, 'hour');
      intervalMinutes = 60;
  }

  let currentTime = startTime;

  while (currentTime.isBefore(endTime)) {
    const requests = randomInRange(50, 500);
    const errorCount = randomInRange(0, Math.floor(requests * 0.05));
    const successCount = requests - errorCount;

    dataPoints.push({
      timestamp: currentTime.unix(),
      requests,
      latency_ms: randomInRange(150, 800),
      success_count: successCount,
      error_count: errorCount,
      tokens: requests * randomInRange(100, 500),
    });

    currentTime = currentTime.add(intervalMinutes, 'minute');
  }

  return dataPoints;
};

// Generate aggregated metrics from data points
export const generateMockMetrics = (
  dataPoints: OCRMetricsDataPoint[]
): OCRMetrics => {
  if (dataPoints.length === 0) {
    return {
      total_requests: 0,
      avg_latency_ms: 0,
      success_rate: 0,
      total_tokens: 0,
      time_range: {
        start: dayjs().unix(),
        end: dayjs().unix(),
      },
    };
  }

  const totalRequests = dataPoints.reduce((sum, dp) => sum + dp.requests, 0);
  const totalLatency = dataPoints.reduce((sum, dp) => sum + dp.latency_ms, 0);
  const totalSuccess = dataPoints.reduce((sum, dp) => sum + dp.success_count, 0);
  const totalTokens = dataPoints.reduce((sum, dp) => sum + dp.tokens, 0);

  return {
    total_requests: totalRequests,
    avg_latency_ms: Math.round(totalLatency / dataPoints.length),
    success_rate: Number(((totalSuccess / totalRequests) * 100).toFixed(2)),
    total_tokens: totalTokens,
    time_range: {
      start: dataPoints[0].timestamp,
      end: dataPoints[dataPoints.length - 1].timestamp,
    },
  };
};

// Generate complete mock metrics response
export const generateMockOCRMetricsResponse = (
  timeRange: OCRTimeRange,
  customMonth?: string
): OCRMetricsResponse => {
  const dataPoints = generateMockMetricsDataPoints(timeRange, customMonth);
  const metrics = generateMockMetrics(dataPoints);

  return {
    metrics,
    data_points: dataPoints,
  };
};
